import {
  getDataTypeForSystemGeneratedColumn,
  getSystemGeneratedColumnTypesFromColumns,
  getWebPath,
  parseSystemColumnFieldId,
  prismaDuplicateTypes,
  SYSTEM_COLUMN_DISPLAY_NAME,
  SYSTEM_GENERATED_COLUMN_TYPES,
  TableColumnForTableExport,
} from '@doss/common-all'
import { Box, CircularProgress, LinearProgress, Stack, Theme, Typography } from '@mui/material'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-premium'
import { find, last, sortBy } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { RxCross2, RxDownload } from 'react-icons/rx'
import {
  BooleanOperator,
  Currency,
  Locale,
  TableFilterInput,
  TableSortInput,
  useCreateTableExportMutation,
  useGetCsvExportTableRowsLazyQuery,
  useGetTableChildAndBacklinkTablesQuery,
  useGetTableRowsQuery,
} from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import useStyles from '~/common/hooks/useStyles'
import { createAndDownloadTableCsv, TableCsvExportOptions } from '~/common/utils/csv'
import { ActiveUser } from '~/components/settings/types'
import { DetailedTable, DetailedTableTab, TableType } from '~/components/tables/types'
import { Button } from '~/components/ui/atoms/button/Button'
import { IconButton } from '~/components/ui/atoms/icon-button/IconButton'
import { LoadingSpinner } from '~/components/ui/atoms/loading-spinner/LoadingSpinner'
import { Modal } from '~/components/ui/molecules/modal/Modal'
import { useBreadcrumbs } from '../../../common/hooks/useBreadcrumbs'
import { useTypedTranslation } from '../../../common/hooks/useTypedTranslation'
import { getFiltersFromTableTab, swapLoggedInUserFilterArgForUserId } from '../../tables/filters'
import { getSortsFromTableTab } from '../../tables/sorts'
import { useTableDetails } from '../../tables/TableDetailsProvider'
import { useDossSnackbar } from '../DossSnackbar'
import { ExportCSVAccordion } from './ExportCSVAccordion'
import { ExportFilterPreview } from './ExportFilterPreview'
import { generateFilterFilenameSegment } from './exportFilterSummary'
import { ExportSortPreview } from './ExportSortPreview'
import {
  EXPORT_SOURCE_ALL_RECORDS,
  EXPORT_SOURCE_CURRENT_VIEW,
  ExportSourceSelector,
  ExportSourceValue,
} from './ExportSourceSelector'
import { useTableMenuContext } from './TableMenus'

const LARGE_TABLE_THRESHOLD = 0

interface ExportTableDialogProps {
  exportTableId: string
  topLevelTable: DetailedTable
  rowCount: number
  tableType: TableType
  selectedTableTab: DetailedTableTab | null
  parentRowId?: string
  backlinkColumnId?: string
  backlinkRowId?: string
  /** Current filters applied in the table view (may include unsaved changes) */
  currentFilters?: TableFilterInput[]
  /** Current sorts applied in the table view (may include unsaved changes) */
  currentSorts?: TableSortInput[]
  /** Current filter boolean operator (AND/OR) from the table view */
  currentFilterBooleanOperator?: BooleanOperator
  /** Current column visibility model from the table view (may include unsaved changes) */
  currentColumnVisibilityModel?: GridColumnVisibilityModel
  /** Whether there are unsaved filter changes that differ from the saved tab */
  hasUnsavedFilters: boolean
  /** Whether there are unsaved sort changes that differ from the saved tab */
  hasUnsavedSorts: boolean
  /** Active users for resolving user IDs to names in filter display */
  activeUsers?: ActiveUser[]
}

function getStyles(theme: Theme) {
  return {
    accordion: {
      backgroundColor: 'transparent',
      width: '100%',
      divider: {
        paddingBlock: theme.spacing(1),
        paddingInline: theme.spacing(1),
      },
      border: `1px solid ${theme.palette.common.doss.gray[4]}`,
      borderRadius: '0px !important',
    },
    accordionSummary: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      paddingBlock: theme.spacing(0),
      paddingInline: theme.spacing(1),
      '&:hover': {
        borderRadius: '4px',
        backgroundColor: theme.palette.common.doss.gray[1],
      },
      '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
      },
      '& .MuiAccordionSummary-content': {
        marginInlineStart: theme.spacing(1),
      },
    },
    accordionDetails: {
      display: 'flex',
      flexDirection: 'column',
      marginInlineStart: theme.spacing(1),
      '& .MuiFormControlLabel-root': {
        paddingInlineStart: theme.spacing(1),
        gap: theme.spacing(1),
        margin: theme.spacing(0),
        width: '100%',
      },
    },
    exportProgress: {
      display: 'flex',
      padding: theme.spacing(1),
    },
    tabSelect: {
      width: '100%',
    },
    tabMenuItem: {
      alignItems: 'center',
    },
    tableName: {
      textDecoration: 'underline',
    },
  }
}

/**
 * Dialog for exporting a table as a CSV
 */
export function ExportTableDialog({
  topLevelTable,
  exportTableId,
  rowCount,
  tableType,
  selectedTableTab,
  parentRowId,
  backlinkColumnId,
  backlinkRowId,
  currentFilters,
  currentSorts,
  currentFilterBooleanOperator,
  currentColumnVisibilityModel,
  hasUnsavedFilters,
  hasUnsavedSorts,
  activeUsers,
}: ExportTableDialogProps) {
  const styles = useStyles(getStyles)
  const { closeMenu } = useTableMenuContext()
  const { breadcrumbs } = useBreadcrumbs()
  const [open, setOpen] = useState(true)
  const [includeRecordId, setIncludeRecordId] = useState(false)
  const { user } = useGlobalContext()
  const { tableTabs } = useTableDetails()
  const { t } = useTypedTranslation()

  const exportTableType = useMemo(() => {
    return topLevelTable.id === exportTableId ? tableType : TableType.CHILD_TABLE
  }, [topLevelTable.id, exportTableId, tableType])

  const isExportingFullChildTable = topLevelTable.id !== exportTableId

  // Fetch full child table data if exporting a child table
  const { data: tableWithChildAndBacklinkTables, loading: tableWithChildAndBacklinkTablesLoading } =
    useGetTableChildAndBacklinkTablesQuery({
      variables: { tableId: topLevelTable.id },
      skip: !isExportingFullChildTable,
    })

  // Fetch child table row count for threshold check when exporting a child table
  // from the parent table page. Uses limit:0 to get just the totalCount.
  const { data: childTableRowCountData } = useGetTableRowsQuery({
    variables: {
      input: {
        tableId: exportTableId,
        filters: [],
        sorts: [],
        pagination: { limit: 0, offset: 0 },
      },
    },
    skip: !isExportingFullChildTable,
  })

  const effectiveRowCount = isExportingFullChildTable
    ? (childTableRowCountData?.paginatedTableRows.totalCount ?? rowCount)
    : rowCount

  const exportTable = isExportingFullChildTable
    ? (tableWithChildAndBacklinkTables?.table.childTables.find((t) => t.id === exportTableId) ??
      tableWithChildAndBacklinkTables?.table.lookupBacklinkTables.find(
        (t) => t.id === exportTableId,
      ))
    : topLevelTable

  // If exporting a child table for a specific parent row, do not show the table tabs.
  const tableTabsForExport = useMemo(() => {
    if (parentRowId) {
      return []
    }

    // When exporting a child table from the parent table, use the parent table's tabs
    return tableTabs
  }, [parentRowId, tableTabs])

  // Create system-generated columns based on saved tab and current visibility
  const systemGeneratedColumns: TableColumnForTableExport[] = useMemo(() => {
    if (!exportTable?.columns) {
      return []
    }

    const selectedTabColumns = selectedTableTab?.columns ?? []

    // Combine system columns from saved tab AND current visibility model
    const tabSystemColumns = getSystemGeneratedColumnTypesFromColumns(selectedTabColumns)
    const visibleSystemColumns = currentColumnVisibilityModel
      ? SYSTEM_GENERATED_COLUMN_TYPES.filter((type) => currentColumnVisibilityModel[type] !== false)
      : []

    // Merge both sets, removing duplicates
    const systemGeneratedColumnTypes = Array.from(
      new Set([...tabSystemColumns, ...visibleSystemColumns]),
    )

    const sortedColumns = sortBy(exportTable.columns, (column) => column.order)
    const lastColumnOrder = last(sortedColumns)?.order ?? 0

    return systemGeneratedColumnTypes.map((systemGeneratedColumnType, index) => {
      const { dataType, indexType } = getDataTypeForSystemGeneratedColumn(systemGeneratedColumnType)
      return {
        id: systemGeneratedColumnType,
        name: SYSTEM_COLUMN_DISPLAY_NAME[systemGeneratedColumnType],
        order: lastColumnOrder + index + 1,
        type: dataType,
        innerType: null,
        indexType,
        resolvedSourceType: dataType,
        indexPrefix: '',
        indexSuffix: '',
        lookupColumn: null,
      }
    })
  }, [exportTable?.columns, selectedTableTab?.columns, currentColumnVisibilityModel])

  // All columns including system-generated ones
  const allTableColumns = useMemo(() => {
    if (!exportTable?.columns) {
      return []
    }
    return [...exportTable.columns, ...systemGeneratedColumns]
  }, [exportTable?.columns, systemGeneratedColumns])

  // All columns that could be referenced by filters (includes parent and child table columns)
  // Used for resolving column names in filter preview display
  const allColumnsForFilterDisplay = useMemo(() => {
    const columns = [...allTableColumns]
    // Add parent table columns when exporting a child table
    if (isExportingFullChildTable) {
      columns.push(...topLevelTable.columns)
    }
    // Add child table columns when exporting a parent table
    if (topLevelTable.childTables.length > 0) {
      columns.push(...topLevelTable.childTables.flatMap((ct) => ct.columns))
    }
    return columns
  }, [allTableColumns, isExportingFullChildTable, topLevelTable])

  // Use current column visibility if available (for unsaved column changes),
  // otherwise fall back to saved tab columns
  const initialSelectedColumns = useMemo(() => {
    if (allTableColumns.length === 0) {
      return []
    }

    // For child tables, select all columns by default (including system columns)
    if (exportTableType === TableType.CHILD_TABLE) {
      return allTableColumns
    }

    // If we have current column visibility model (may include unsaved changes), use it
    if (currentColumnVisibilityModel && Object.keys(currentColumnVisibilityModel).length > 0) {
      return allTableColumns.filter((column) => {
        // GridColumnVisibilityModel: undefined or true = visible, false = hidden
        const isVisible = currentColumnVisibilityModel[column.id]
        return isVisible !== false
      })
    }

    // Otherwise, use saved tab columns
    if (selectedTableTab) {
      return allTableColumns.filter((column) => {
        // Check if it's a system-generated column
        const isSystemColumn = parseSystemColumnFieldId(column.id)
        if (isSystemColumn) {
          // System columns are included if they're in our systemGeneratedColumns list
          return systemGeneratedColumns.some((sysCol) => sysCol.id === column.id)
        }

        // Regular columns: check if they're in the saved tab
        return selectedTableTab.columns.some((tabColumn) => tabColumn.tableColumn?.id === column.id)
      })
    }

    // Default: all columns
    return allTableColumns
  }, [
    allTableColumns,
    exportTableType,
    currentColumnVisibilityModel,
    selectedTableTab,
    systemGeneratedColumns,
  ])

  // For child tables, select all columns by default
  const [selectedColumns, setSelectedColumns] =
    useState<TableColumnForTableExport[]>(initialSelectedColumns)
  const [progress, setProgress] = useState(0)
  const [exportStarted, setExportStarted] = useState(false)
  const [getPaginatedTableData] = useGetCsvExportTableRowsLazyQuery()
  const snackbar = useDossSnackbar()
  const [currentAbortController, setCurrentAbortController] = useState<AbortController | null>(null)
  const [createTableExportMutation] = useCreateTableExportMutation()

  // Unified export source selection: current view, a specific tab, or all records
  // Default to current view if there are unsaved filters or sorts, otherwise default to selected tab or all
  const hasUnsavedChanges = hasUnsavedFilters || hasUnsavedSorts
  const getInitialExportSource = (): ExportSourceValue => {
    if (hasUnsavedChanges) {
      return EXPORT_SOURCE_CURRENT_VIEW
    }
    if (selectedTableTab?.id) {
      return selectedTableTab.id
    }
    return EXPORT_SOURCE_ALL_RECORDS
  }
  const [exportSource, setExportSource] = useState<ExportSourceValue>(getInitialExportSource)

  // Derive whether we're exporting current view from the unified source
  const exportCurrentView = exportSource === EXPORT_SOURCE_CURRENT_VIEW

  // Get the selected tab for export based on the source selection
  const selectedTabIdForExport =
    exportSource === EXPORT_SOURCE_CURRENT_VIEW || exportSource === EXPORT_SOURCE_ALL_RECORDS
      ? 0
      : exportSource

  // This is used to manage the start of the export process when the user clicks Export button,
  // range of the progress is 0 - 100, we start at 5 to indicate something is happening.
  const handleStartExport = async (props: TableCsvExportOptions, includeRecordId: boolean) => {
    const abortController = new AbortController()
    setCurrentAbortController(abortController)

    setExportStarted(true)
    setProgress(5)
    await createAndDownloadTableCsv(
      props,
      includeRecordId,
      getPaginatedTableData,
      setProgress,
      abortController.signal,
      user?.organizationLocale ?? Locale.EN_US,
      user?.organizationTimeZone ?? 'America/Los_Angeles',
      user?.organizationCurrency ?? Currency.USD,
      exportTableType,
      user?.id,
    )
      .catch(() => {
        snackbar.showError(t('table.error_exporting_table'))
      })
      .finally(() => {
        setProgress(0)
        setExportStarted(false)
        handleClose()
      })
  }

  const selectedTabForExport = useMemo(() => {
    const tab = find(tableTabsForExport, (tableTab) => {
      return tableTab.id === selectedTabIdForExport
    })
    if (!tab) {
      return null
    }
    return tab
  }, [selectedTabIdForExport, tableTabsForExport])

  // Sync selected columns when initial selection changes (e.g., when column visibility model changes)
  useEffect(() => {
    setSelectedColumns(initialSelectedColumns)
  }, [initialSelectedColumns])

  const handleCancelExport = () => {
    if (currentAbortController) {
      currentAbortController.abort()
      setCurrentAbortController(null)
      snackbar.showInfo(t('table.csv_export_cancelled'))
    }
    setProgress(0)
    setExportStarted(false)
  }

  const handleStartServerExport = async () => {
    if (!exportTable) {
      return
    }

    // When exporting from the record view, the tab selector is hidden (tableTabsForExport
    // is empty) so selectedTabForExport is null. Fall back to the prop-provided active tab
    // so sorts/filters from the current view are still applied.
    // IMPORTANT: Do not fall back to selectedTableTab when explicitly exporting all records.
    const effectiveTab =
      exportSource === EXPORT_SOURCE_ALL_RECORDS ? null : (selectedTabForExport ?? selectedTableTab)

    // Build filters/sorts from selected tab, matching client-side logic
    let columnsForFiltering = exportTable.columns
    if (isExportingFullChildTable && effectiveTab) {
      columnsForFiltering = [
        ...topLevelTable.columns,
        ...topLevelTable.childTables.flatMap((ct) => ct.columns),
      ]
    } else if (!isExportingFullChildTable && topLevelTable.childTables.length > 0) {
      columnsForFiltering = [
        ...exportTable.columns,
        ...topLevelTable.childTables.flatMap((ct) => ct.columns),
      ]
    }
    const { cleanedFilters } = getFiltersFromTableTab({
      selectedTab: effectiveTab,
      tableColumns: columnsForFiltering,
      tableId: exportTable.id,
    })

    const { cleanedSorts } = getSortsFromTableTab(effectiveTab)

    // When exporting current view, use the passed-in current filters/sorts instead of saved tab
    const filtersToExport = exportCurrentView ? (currentFilters ?? []) : cleanedFilters
    const sortsToExport = exportCurrentView ? (currentSorts ?? []) : cleanedSorts
    const booleanOperatorToExport = exportCurrentView
      ? currentFilterBooleanOperator
      : effectiveTab?.booleanOperator

    // Generate a descriptive filename based on the export source
    let exportFileName = exportPath
    if (exportCurrentView && filtersToExport.length > 0) {
      // Include filter summary in filename for current view exports
      const filterSegment = generateFilterFilenameSegment(filtersToExport, columnsForFiltering)
      if (filterSegment) {
        exportFileName = `${exportPath}-${filterSegment}`
      } else {
        exportFileName = `${exportPath}-filtered`
      }
    } else if (effectiveTab) {
      exportFileName = `${exportPath}-${effectiveTab.name}`
    }
    const effectiveFilters = swapLoggedInUserFilterArgForUserId(filtersToExport, user?.id)

    try {
      await createTableExportMutation({
        variables: {
          input: {
            tableId: exportTable.id,
            columnIds: selectedColumns.map((col) => col.id),
            includeRecordId,
            fileName: `${exportFileName}.csv`,
            filters: effectiveFilters,
            sorts: sortsToExport,
            filterBooleanOperator: booleanOperatorToExport,
            tableTabId: effectiveTab?.id,
            parentRowId,
            backlinkColumnId,
            backlinkRowId,
            exportChildWithParentRow:
              isExportingFullChildTable || (backlinkColumnId != null && backlinkRowId != null),
          },
        },
      })
      const exportsPagePath = `${getWebPath({
        objectType: prismaDuplicateTypes.BreadcrumbObjectType.TABLE,
        objectId: exportTable.id,
      })}/exports`
      snackbar.showInfo(t('table.csv_export_started'), exportsPagePath)
      handleClose()
    } catch {
      snackbar.showError(t('table.error_exporting_table'))
    }
  }

  const handleClose = () => {
    closeMenu()
    setOpen(false)
  }

  if (isExportingFullChildTable && tableWithChildAndBacklinkTablesLoading) {
    return (
      <Modal actions={[]} fullWidth onClose={handleClose} open={open}>
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
          <LoadingSpinner type="search" />
        </Box>
      </Modal>
    )
  }

  if (!exportTable) {
    return null
  }

  // This is used to manage the column state changes so that we can pass the only selected columns
  // to the CSV exporter
  const handleColumnChange = (column: TableColumnForTableExport, isChecked: boolean) => {
    if (isChecked) {
      setSelectedColumns((prev) => [...prev, column])
    } else {
      setSelectedColumns((prev) => prev.filter((col) => col.id !== column.id))
    }
  }

  let exportPath = exportTable.name
  if (breadcrumbs.length > 0) {
    const exportPathParents = breadcrumbs
      .slice(-2)
      .map((crumb) => crumb.name)
      .join('/')

    exportPath = `${exportPathParents}/${exportTable.name}`
  }

  const getDialogActions = () => {
    if (exportStarted) {
      return undefined
    }
    return (
      <>
        <Button
          onClick={() => {
            if (effectiveRowCount >= LARGE_TABLE_THRESHOLD) {
              handleStartServerExport()
            } else {
              handleStartExport(
                {
                  id: exportTable.id,
                  name: exportTable.name,
                  selectedColumns,
                  rowCount,
                  handleCloseDialog: handleClose,
                  exportPath: selectedTabForExport
                    ? `${exportPath}-${selectedTabForExport.name}`
                    : exportPath,
                  tableTabForExport:
                    !backlinkColumnId && selectedTabForExport ? selectedTabForExport : undefined,
                  parentRowId,
                  backlinkColumnId,
                  backlinkRowId,
                  parentTableColumns: isExportingFullChildTable ? topLevelTable.columns : undefined,
                  allColumns: allTableColumns,
                  // Include child table columns when exporting a parent table to support filters on child columns
                  childTableColumns:
                    topLevelTable.childTables.length > 0
                      ? topLevelTable.childTables.flatMap((childTable) => childTable.columns)
                      : undefined,
                },
                includeRecordId,
              )
            }
          }}
          size="medium"
          startIcon={<RxDownload />}
          type="button"
          variant="secondary"
        >
          Export
        </Button>
      </>
    )
  }

  return (
    <Modal
      actions={getDialogActions()}
      fullWidth
      onClose={handleClose}
      open={open}
      title={t('table.exporting_from', { tableName: exportTable.name })}
    >
      <Stack direction="column" spacing={2}>
        {/* Unified source selector - shows current view option prominently when there are unsaved filters */}
        {tableTabsForExport.length > 0 && (
          <ExportSourceSelector
            currentFilterCount={currentFilters?.length ?? 0}
            hasUnsavedChanges={hasUnsavedChanges}
            onValueChange={setExportSource}
            selectedValue={exportSource}
            tableName={exportTable.name}
            tableTabs={tableTabsForExport}
          />
        )}

        {/* Show filter preview when exporting current view with unsaved filter changes */}
        {exportCurrentView && hasUnsavedFilters && (
          <ExportFilterPreview
            activeUsers={activeUsers}
            booleanOperator={currentFilterBooleanOperator}
            columns={allColumnsForFilterDisplay}
            filters={currentFilters ?? []}
          />
        )}

        {/* Show sort preview when exporting current view with unsaved sort changes */}
        {exportCurrentView && hasUnsavedSorts && (
          <ExportSortPreview columns={allColumnsForFilterDisplay} sorts={currentSorts ?? []} />
        )}

        <ExportCSVAccordion
          columnHeaders={exportTable.columns}
          handleColumnChange={handleColumnChange}
          name={exportTable.name}
          selectedColumns={selectedColumns}
          selectedTabForExport={selectedTabForExport}
          setIncludeRecordId={setIncludeRecordId}
          setSelectedColumns={setSelectedColumns}
        />
      </Stack>
      {exportStarted && (
        <Box alignItems="center">
          <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
            <Stack alignItems="center" direction="row" gap={1} sx={styles.exportProgress}>
              <Typography variant="body1">{t('table.exporting_records')}</Typography>
              <CircularProgress color="success" disableShrink size={14} thickness={6} />
            </Stack>
            <IconButton onClick={handleCancelExport} size="small" variant="ghost">
              <RxCross2 />
            </IconButton>
          </Stack>
          <LinearProgress color="success" value={progress} variant="determinate" />
        </Box>
      )}
    </Modal>
  )
}
